import { useEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

let registered = false;

function registerGsap() {
  if (registered) return;
  gsap.registerPlugin(ScrollTrigger);
  registered = true;
}

function prefersReducedMotion() {
  return typeof window !== "undefined"
    && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function isMobileViewport() {
  return typeof window !== "undefined"
    && window.matchMedia("(max-width: 767px)").matches;
}

export function useGsapReveal(routeKey) {
  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    registerGsap();

    // Hero ambient motion: GSAP drives ONLY the Ken Burns image movement.
    // The film grain is CSS-only (transform jitter, constant opacity) and
    // is intentionally shown to all users per brand direction.
    const reduceMotion = prefersReducedMotion();

    const ctx = gsap.context(() => {
      const mobile = isMobileViewport();
      const cinematicEase = "power3.out";

      if (!reduceMotion) {
        gsap.fromTo(
          "[data-gsap='header']",
          { autoAlpha: 0, y: -14 },
          { autoAlpha: 1, y: 0, duration: 0.7, ease: cinematicEase, clearProps: "transform" }
        );

        gsap.fromTo(
          "[data-gsap='page']",
          { autoAlpha: 0, y: 16 },
          { autoAlpha: 1, y: 0, duration: 0.65, ease: cinematicEase, delay: 0.05, clearProps: "transform" }
        );
      }

      const hero = document.querySelector("[data-gsap='hero']");
      if (hero) {
        const heroItems = hero.querySelectorAll(
          "[data-gsap='hero-kicker'], [data-gsap='hero-title'], [data-gsap='hero-subtitle'], [data-gsap='hero-cta']"
        );

        if (!reduceMotion) {
          gsap.fromTo(
            heroItems,
            { autoAlpha: 0, y: 26 },
            {
              autoAlpha: 1,
              y: 0,
              duration: 0.9,
              ease: cinematicEase,
              stagger: 0.11,
              delay: 0.15,
              clearProps: "transform",
            }
          );
        }

        const media = hero.querySelector("[data-gsap='hero-media']");
        if (media) {
          // Cinematic Ken Burns — runs for ALL users (brand direction),
          // including reduced-motion, but kept slow & gentle. GSAP owns the
          // transform on this element entirely (no CSS animation writes
          // transform here) so there is no conflict. Continuous slow zoom +
          // horizontal pan + slight vertical drift, looping with yoyo so it
          // never snaps. Premium values: scale 1.06 -> 1.14, pan ±2%.
          gsap.set(media, { transformOrigin: "center center", willChange: "transform" });

          // Gentle fade-in (opacity only) — does not disturb the transform loop.
          gsap.fromTo(media, { autoAlpha: 0 }, { autoAlpha: 1, duration: 1.2, ease: "power2.out" });

          // The looping movement. Mobile keeps a slightly gentler pan for perf
          // but is NOT disabled.
          gsap.fromTo(
            media,
            { scale: 1.06, xPercent: mobile ? -1 : -2, yPercent: 0 },
            {
              scale: 1.14,
              xPercent: mobile ? 1 : 2,
              yPercent: -1.5,
              duration: mobile ? 16 : 18,
              ease: "sine.inOut",
              repeat: -1,
              yoyo: true,
            }
          );
        }
      }

      if (!reduceMotion) {
      gsap.utils.toArray("[data-gsap='reveal']").forEach((el) => {
        gsap.fromTo(
          el,
          { autoAlpha: 0, y: mobile ? 18 : 34 },
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.85,
            ease: cinematicEase,
            clearProps: "transform",
            scrollTrigger: {
              trigger: el,
              start: "top 86%",
              once: true,
            },
          }
        );
      });

      gsap.utils.toArray("[data-gsap='stagger']").forEach((container) => {
        const children = Array.from(container.children).filter((child) => child.offsetParent !== null);
        if (!children.length) return;
        gsap.fromTo(
          children,
          { autoAlpha: 0, y: mobile ? 16 : 30 },
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.75,
            ease: cinematicEase,
            stagger: mobile ? 0.045 : 0.075,
            clearProps: "transform",
            scrollTrigger: {
              trigger: container,
              start: "top 86%",
              once: true,
            },
          }
        );
      });

      gsap.utils.toArray("[data-gsap='image']").forEach((el) => {
        gsap.fromTo(
          el,
          { autoAlpha: 0, scale: mobile ? 0.995 : 0.975 },
          {
            autoAlpha: 1,
            scale: 1,
            duration: 0.85,
            ease: "power2.out",
            clearProps: "transform",
            scrollTrigger: {
              trigger: el,
              start: "top 88%",
              once: true,
            },
          }
        );
      });
      }

      ScrollTrigger.refresh();
    }, document.body);

    // Recompute trigger positions once images/fonts settle, so scroll
    // reveals fire at the right scroll points (not mis-measured early).
    const onLoad = () => {
      if (typeof ScrollTrigger?.refresh === "function") ScrollTrigger.refresh();
    };
    window.addEventListener("load", onLoad);

    return () => {
      window.removeEventListener("load", onLoad);
      ctx.revert();
    };
  }, [routeKey]);
}
